import { $DepartureSchedule } from "@store/departure-schedule-store";
import {
  DepartureScheduleCalendarData,
  DepartureScheduleCalendarDay,
} from "@interfaces/departure-schedule";
import DepartureScheduleService from "@services/departure-schedule-service";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";

import React, { FC, useState, memo, useMemo } from "react";
import moment, { Moment } from "moment";
import { styled } from "@mui/material/styles";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import Typography from "@mui/material/Typography";
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import { CalendarCheckIcon, CarProfileIcon } from "@phosphor-icons/react";
import { useDepartureScheduleQuery } from "@lib/daparture-schedule";
import { useUnit } from "effector-react";
import useModal from "@modals/modal-hook";
import clsx from "clsx";
import style from "@scss/pages/company/departure-schedule.module.scss";

type DayColoredProps = {
  props: PickersDayProps<Moment>
  data?: DepartureScheduleCalendarDay
};

interface Props {
  isLoading: boolean;
  query: ReturnType<typeof useDepartureScheduleQuery>
}

const DepartureScheduleCalendar: FC<Props> = memo(({ isLoading, query }) => {
  const departureSchedule = useUnit($DepartureSchedule);
  const [open, setOpen] = useState<boolean>(false);
  const [content, setContent] = useState<React.ReactElement>();
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
  });

  const handleClick = (event?: React.MouseEvent<HTMLElement> | undefined) => {
    if(event && event.currentTarget){
      const data = moment(+event.currentTarget.dataset['timestamp']!);
      const findDate = dateArray[data.month()];
      if(findDate.days.length > 0){
        setContent(
          <>
            {findDate.data.filter(с => с.dates.filter(d => moment(d.date, 'DD.MM').get('D') === data.get('D')).length > 0).map((d, i) => (
              <Stack direction="column" justifyContent="start" spacing={2} sx={{ padding: 1, ...(findDate.data.length > 0 && i > 0 ? { pt: 0 } : {}) }}>
                <Typography>{d.company}</Typography>
                {d.dates && d.dates.filter(d => moment(d.date, 'DD.MM').date() === data.date()).map(date =>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 0,
                      mt: '0 !important',
                      color: date.type === "success" ? 'var(--color-green) !important;' : 
                            date.type === "warning" ? 'var(--color-orange) !important;' : 
                            date.type === "planned" ? 'var(--color-blue-light) !important;' : 
                            'inherit'
                    }}
                  >
                    {moment(date.date, 'DD.MM').format('D MMM YYYY г.')}
                  </Typography>
                )}
              </Stack>
            ))}
            <Btns query={query} setOpen={setOpen} date={data.toISOString()} />
          </>
        )
      }else{
        setContent(<Btns query={query} setOpen={setOpen} date={data.toISOString()} />)
      }
      setOpen(true);
      setPosition({
        top: event.clientY - 5,
        left: event.clientX,
      })
    }
  };

  const dateArray = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const startMonth = moment().startOf("year").add(i, "M");
      const minDate = moment(startMonth).startOf("M");
      const maxDate = moment(startMonth).endOf("M");
      const filterData = departureSchedule.filter(e => 
        e.periods.filter(p => 
          p.dates.filter(d => moment(d.date, 'DD.MM').isBetween(minDate, maxDate, 'month', '[]')).length > 0
        ).length > 0
      );
      
      const data = filterData.map(e => {
        const period = e.periods.find(p => p.dates.filter(d => moment(d.date, 'DD.MM').isBetween(minDate, maxDate, 'day', '[]')).length > 0);
        const searchPlannedDates = period && period.dates.filter(f => f.type === 'planned' || f.type === 'warning' || f.type === 'success').length > 0;
        return {
          company: e.company.name,
          dates: searchPlannedDates ? period.dates.filter(f => f.type === 'planned' || f.type === 'warning' || f.type === 'success') : [],
        } as DepartureScheduleCalendarData;
      })
  
      const days = [] as DepartureScheduleCalendarDay[];
      data.forEach(e => {
        e.dates.forEach(d => {
          days.push({
            day: moment(d.date, 'DD.MM'),
            status: d.type
          })
        })
      })
      
      return {
        date: startMonth,
        minDate: minDate,
        maxDate: maxDate,
        data: data.filter(e => e.dates && e.dates.filter(d => d.date !== null).length > 0),
        days: days.sort((a, b) => a.status === "success" || a.status === "warning" ? -1 : 0)
      };
    })
  }, [departureSchedule]);

  return (
    <Box
      rowGap={0}
      columnGap={0}
      display="grid"
      gridTemplateColumns={{
        xs: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
        xl: 'repeat(4, 1fr)',
      }}
    >
      {dateArray.map((e) => (
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={"ru"}
          localeText={
            ruRU.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DateCalendar
            disableHighlightToday
            readOnly
            openTo="month"
            referenceDate={e.date}
            views={["day"]}
            minDate={e.minDate}
            maxDate={e.maxDate}
            slots={{
              calendarHeader: CustomCalendarHeader,
              day: (props: PickersDayProps<Moment>) => 
                <DayColored
                  props={props}
                  data={e.days.find(d => d.day.date() === props.day.date())}
                />
            }}
            slotProps={{
              day: {
                onClick: handleClick
              }
            }}
          />
        </LocalizationProvider>
      ))}
      <Popover
        anchorReference="anchorPosition"
        open={open}
        onClose={() => setOpen(false)}
        anchorPosition={position}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {content}
      </Popover>
    </Box>
  );
});

const DayColored: FC<DayColoredProps> = memo(({ props, data }) => {
  const { day, outsideCurrentMonth, ...other } = props;

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      className={clsx({
        [style.departure_schedule_calendar_success]: data?.status === "success",
        [style.departure_schedule_calendar_warning]: data?.status === "warning",
        [style.departure_schedule_calendar_planned]: data?.status === "planned",
        [style.departure_schedule_calendar_danger]: data?.status === "danger"
      })}
    />
  );
})

const Btns: FC<{ query: ReturnType<typeof useDepartureScheduleQuery>, setOpen: React.Dispatch<React.SetStateAction<boolean>>, date: string }> = memo(({ query, setOpen, date }) => {
  const permissions = useUnit($UserAddPermissions);
  const { open } = useModal();

  const handleAddDepartureHistory = (type: "planned" | "full", date: string) => {
    setOpen(false);
    open("DepartureScheduleHistoryCreate", {
      btnText: "Сохранить",
      modalData: {
        title: type === "planned" ? 'Укажите планируемую дату выезда в компанию' : 'Данные выезда',
        type: type,
        isCompany: true,
        date: date,
        dateDaparture: date
      },
      onConfirm: (date: Moment, companyId: number, prescriptionId: number, dateDaparture: Moment, comment: string, files: File[]) => {
        const formData = new FormData();
        formData.append('companyId', companyId.toString());
        if(type === "planned"){
          formData.append('date', date.format("YYYY-MM-DD"));
        }else if(type === "full"){
          formData.append('prescriptionId', prescriptionId.toString());
          formData.append('date', dateDaparture.format("YYYY-MM-DD"));
          formData.append('comment', comment);
          if(files.length > 0){
            formData.append('file', files[0]);
          }
        }
        DepartureScheduleService.historyCreate(formData, (err, res) => {
          if (err || !res) {
            return Error("При добавлении выезда произошла ошибка");
          }
          if (res.data) {
            query.refetch();
          }
        });
      },
    });
  };

  return (
    <>
      <Tooltip title="Запланировать выезд" placement="top">
        <IconButton
          onClick={(e) => {
            handleAddDepartureHistory("planned", date);
          }}
          className={clsx(style.departure_schedule_row_collapseIcon)}
        >
          <WrapperSVG color="blue">
            <CarProfileIcon size={20} />
          </WrapperSVG>
        </IconButton>
      </Tooltip>
      {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToCreateHistory) && (
        <Tooltip title="Добавить выезд" placement="top">
          <IconButton
            onClick={(e) => {
              handleAddDepartureHistory("full", date);
            }}
            className={clsx(style.departure_schedule_row_collapseIcon)}
          >
            <WrapperSVG color="green">
              <CalendarCheckIcon size={20} />
            </WrapperSVG>
          </IconButton>
        </Tooltip>
      )}
    </>
  )
})

const CustomCalendarHeaderRoot = styled("div")({
  display: "flex",
  justifyContent: "center",
  padding: "8px 16px",
  alignItems: "center",
});

function CustomCalendarHeader(props: any) {
  const { currentMonth } = props;

  return (
    <CustomCalendarHeaderRoot>
      <Typography variant="body2">
        {currentMonth.format("MMMM YYYY")}
      </Typography>
    </CustomCalendarHeaderRoot>
  );
}

export default DepartureScheduleCalendar;
